/*** Swarm RTS Effects module - (c) mjh.at - v0.3.0 2014-12-28 ***/

/**
 * Routes effect-spawn calls to the active renderer's backend-specific factory.
 *
 * Call sites use effectSystem.spawn(type, ...args).
 * Game owns ONE EffectSystem for its whole lifetime — loadLevel clears it
 * rather than replacing it — so the active renderer registers its factories
 * exactly once, in its constructor, via setFactories(map, onClear).
 *
 * Canvas2D effects are drawn via EffectSystem.updateByLayer(ctx, dt, layer).
 * WebGL effects are managed by the renderer's own typed sync arrays — they
 * never enter EffectSystem.effects.
 */
class EffectSystem {
    constructor() {
        /** @type {import('./render/canvas2d/effects.js').CanvasEffect[]} */
        this.effects = [];
        /**
         * Factory map registered by the active renderer.
         * Keys are effect type strings ('Explosion', 'Fireworks', etc.).
         * Canvas2D factories return a CanvasEffect; WebGL factories push into
         * renderer-internal typed arrays and return null.
         * @type {Record<string, (...args: unknown[]) => import('./render/canvas2d/effects.js').CanvasEffect|null>|null}
         */
        this._factories = null;
        /**
         * Backend hook invoked by clear() — the WebGL renderer drops its
         * per-level GPU effect pools here. @type {(() => void)|null}
         */
        this._onBackendClear = null;
    }

    /**
     * Register backend-specific effect factories.
     * Called once by each renderer on construction (a renderer rebuild
     * re-registers, replacing the previous backend's map and hook).
     * Clears any in-flight Canvas2D effects so stale objects don't persist
     * after switching backends.
     * @param {Record<string, (...args: unknown[]) => import('./render/canvas2d/effects.js').CanvasEffect|null>|null} map
     * @param {(() => void)|null} [onClear]  Invoked by clear() so the backend
     *   can drop effect state it holds outside this.effects.
     */
    setFactories(map, onClear = null) {
        this.effects.length = 0;
        this._factories = map;
        this._onBackendClear = onClear;
    }

    /**
     * Drop all in-flight effects (both the Canvas2D list and, via the backend
     * hook, any renderer-internal pools). Called by Game.loadLevel — the
     * EffectSystem instance itself lives for the whole Game, so factory
     * registration survives level loads.
     */
    clear() {
        this.effects.length = 0;
        this._onBackendClear?.();
    }

    /**
     * Spawn an effect by type name.
     * If no factory is registered (headless/test mode with no renderer) the
     * call is silently dropped.
     * @param {string} type  'Explosion' | 'Fireworks' | 'IconEffect' | 'PowerImpactCircle'
     * @param {...unknown} args  Constructor arguments forwarded to the factory
     */
    spawn(type, ...args) {
        const factory = this._factories?.[type];
        if (!factory) return;
        const effect = factory(...args);
        // WebGL factories return null (routed to typed arrays in the renderer);
        // Canvas2D factories return the effect object for updateByLayer to drive.
        if (effect !== null && effect !== undefined && this.effects.length < 1000) {
            this.effects.push(effect);
        }
    }

    /**
     * Advance and draw all effects on the given layer, removing finished ones.
     * Used by the Canvas2D renderer only; WebGL uses its own sync methods.
     * @param {CanvasRenderingContext2D} ctx
     * @param {number} dt
     * @param {'top'|'ground'|'mid'} layer
     */
    updateByLayer(ctx, dt, layer) {
        const fx = this.effects;
        let i = 0;
        while (i < fx.length) {
            const effect = fx[i];
            if (effect.layer === layer) {
                effect.update(ctx, dt);
                if (effect.isDone()) {
                    fx[i] = fx[fx.length - 1];
                    fx.pop();
                    continue;
                }
            }
            i += 1;
        }
    }
}

export { EffectSystem };
